import { useState, useEffect, useCallback } from 'react';
import { PurchasesPackage } from 'react-native-purchases';
import revenueCatService from '../services/revenueCatService';
import { OfferingMetadata, getLocalizedString, DEFAULT_LOCALE } from '../types/revenueCat';

interface UseOfferingsResult {
  packages: PurchasesPackage[];
  metadata: OfferingMetadata;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  getTitle: (locale?: string) => string;
  getSubtitle: (locale?: string) => string;
  getCTA: (locale?: string) => string;
}

export function useOfferings(): UseOfferingsResult {
  const [packages, setPackages] = useState<PurchasesPackage[]>([]);
  const [metadata, setMetadata] = useState<OfferingMetadata>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchOfferings = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const result = await revenueCatService.getOfferings();

      if (result.current) {
        setPackages(result.current.packages);
        setMetadata(result.current.metadata || {});
        console.log('✅ useOfferings: Offerings loaded', {
          packagesCount: result.current.packages.length,
          hasMetadata: !!result.current.metadata,
        });
      } else {
        setPackages([]);
        setMetadata({});
        console.log('⚠️ useOfferings: No current offering available');
      }
    } catch (err) {
      const error = err as Error;
      console.error('❌ useOfferings: Failed to fetch offerings:', error);
      setError(error);
      setPackages([]);
      setMetadata({});
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOfferings();
  }, [fetchOfferings]);

  const getTitle = useCallback(
    (locale: string = DEFAULT_LOCALE) => {
      return getLocalizedString(
        metadata.title_strings,
        locale,
        'Padrinho Plus'
      );
    },
    [metadata.title_strings]
  );

  const getSubtitle = useCallback(
    (locale: string = DEFAULT_LOCALE) => {
      return getLocalizedString(
        metadata.subtitle_strings,
        locale,
        'Desbloqueie todos os recursos premium'
      );
    },
    [metadata.subtitle_strings]
  );

  const getCTA = useCallback(
    (locale: string = DEFAULT_LOCALE) => {
      return getLocalizedString(
        metadata.cta_strings,
        locale,
        'Fazer upgrade para Plus'
      );
    },
    [metadata.cta_strings]
  );

  return {
    packages,
    metadata,
    isLoading,
    error,
    refetch: fetchOfferings,
    getTitle,
    getSubtitle,
    getCTA,
  };
}
